import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

import { isSuperadminRole } from 'core/router/routes';
import { navGroups, type NavItem } from './navItems';

type SidebarProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

const Sidebar = ({ collapsed, onToggleCollapsed }: SidebarProps) => {
  const { pathname } = useLocation();
  const userRole = useSelector((state: any) => state.reducer.role);
  const isAdmin = useSelector((state: any) => state.reducer.isAdmin);
  const user = useSelector((state: any) => state.reducer.user?.data);

  const isItemAllowed = useCallback(
    (item: NavItem) => {
      if (!userRole) return false;
      if (isSuperadminRole(userRole, isAdmin)) return true;
      if (item.adminOnly) return isAdmin;
      if (item.allRoles) return true;
      if (!item.permission) return true;
      const allowed = userRole.permissions.map((p: any) => p.name);
      return allowed.includes(item.permission);
    },
    [userRole, isAdmin]
  );

  const visibleGroups = useMemo(
    () =>
      navGroups
        .map((g) => ({ ...g, items: g.items.filter(isItemAllowed) }))
        .filter((g) => g.items.length > 0),
    [isItemAllowed]
  );

  const isItemActive = (path: string) =>
    pathname === path || (path !== '/' && pathname.startsWith(path));

  return (
    <aside
      data-collapsed={collapsed}
      style={{
        width: collapsed ? 'var(--shell-sidebar-w-collapsed)' : 'var(--shell-sidebar-w)',
        background: 'var(--shell-bg-sidebar)',
        borderRight: '1px solid var(--shell-border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: `width var(--shell-transition-base)`,
        overflow: 'hidden',
      }}
    >
      {/* Логотип / brand */}
      <div
        style={{
          height: 'var(--shell-topbar-h)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '0' : '0 14px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid var(--shell-border-subtle)',
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/logo.svg`}
          alt="Контекст"
          width={26}
          height={26}
          style={{
            width: 26,
            height: 26,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 0.2,
              color: 'var(--shell-text-primary)',
            }}
          >
            Контекст
          </span>
        )}
      </div>

      {/* Nav */}
      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: collapsed ? '8px 6px' : '8px 8px',
        }}
      >
        {visibleGroups.map((group, gi) => (
          <div key={group.id} style={{ marginTop: gi === 0 ? 0 : 16 }}>
            {!collapsed && (
              <div
                style={{
                  padding: '6px 10px 4px',
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  color: 'var(--shell-text-tertiary)',
                }}
              >
                {group.label}
              </div>
            )}
            {collapsed && gi > 0 && (
              <div
                style={{
                  height: 1,
                  background: 'var(--shell-border-subtle)',
                  margin: '8px 6px',
                }}
              />
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {group.items.map((item) => {
                const active = isItemActive(item.path);
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <Link
                      to={item.path}
                      title={collapsed ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        height: 'var(--shell-row-h)',
                        padding: collapsed ? '0' : '0 10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        margin: '1px 0',
                        borderRadius: 'var(--shell-radius-md)',
                        textDecoration: 'none',
                        color: active ? 'var(--shell-text-primary)' : 'var(--shell-text-secondary)',
                        background: active ? 'var(--shell-bg-selected)' : 'transparent',
                        fontSize: 13,
                        fontWeight: active ? 500 : 400,
                        position: 'relative',
                        transition: `background var(--shell-transition-fast), color var(--shell-transition-fast)`,
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLElement).style.background =
                            'var(--shell-bg-hover)';
                          (e.currentTarget as HTMLElement).style.color =
                            'var(--shell-text-primary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                          (e.currentTarget as HTMLElement).style.color =
                            'var(--shell-text-secondary)';
                        }
                      }}
                    >
                      {active && (
                        <span
                          aria-hidden
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 2,
                            borderRadius: 2,
                            background: 'var(--shell-accent-primary)',
                          }}
                        />
                      )}
                      <Icon
                        size={16}
                        strokeWidth={1.75}
                        color={
                          active ? 'var(--shell-accent-primary)' : 'var(--shell-text-tertiary)'
                        }
                        style={{ flexShrink: 0 }}
                      />
                      {!collapsed && (
                        <span
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Подвал: пользователь + collapse */}
      <div
        style={{
          borderTop: '1px solid var(--shell-border-subtle)',
          padding: collapsed ? '6px' : '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {!collapsed && user && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 8px',
              borderRadius: 'var(--shell-radius-md)',
            }}
          >
            <div
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'var(--shell-bg-subtle)',
                color: 'var(--shell-text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {(user.firstName?.[0] ?? user.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--shell-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.firstName ? `${user.firstName} ${user.lastName ?? ''}` : user.email}
              </div>
              {user.email && user.firstName && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--shell-text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={onToggleCollapsed}
          title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            height: 28,
            padding: collapsed ? 0 : '0 10px',
            borderRadius: 'var(--shell-radius-md)',
            border: 'none',
            background: 'transparent',
            color: 'var(--shell-text-secondary)',
            cursor: 'pointer',
            fontSize: 12,
            transition: `background var(--shell-transition-fast)`,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          {!collapsed && <span>Свернуть</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
